import React, { useState, useEffect } from "react";
import axios from "axios";
import { BarChart3, TrendingUp, MapPin, Fuel, Users, ChevronDown, ChevronUp } from "lucide-react";
import { API, FUEL_TYPES, formatCurrency } from "./constants";

export default function CommunityStats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/analytics/community-stats`)
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || !stats || stats.total_trips === 0) return null;

  const topFuel = Object.entries(stats.fuel_type_breakdown || {}).sort((a, b) => b[1] - a[1])[0];

  return (
    <div className="sf-card p-5" data-testid="community-stats-panel">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between"
        data-testid="community-stats-toggle"
      >
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-[#E52020]" />
          <span className="text-xs font-['Manrope'] font-bold text-[#666666] tracking-wider">COMMUNITY STATS</span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-[#999999]" /> : <ChevronDown className="w-4 h-4 text-[#999999]" />}
      </button>

      {/* Summary row — always visible */}
      <div className="flex items-center gap-4 mt-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          <BarChart3 className="w-3.5 h-3.5 text-[#16A34A]" />
          <span className="text-sm font-mono font-bold text-[#1A1A1A]">{stats.total_trips.toLocaleString()}</span>
          <span className="text-xs text-[#666666]">trips</span>
        </div>
        <div className="flex items-center gap-1.5">
          <TrendingUp className="w-3.5 h-3.5 text-[#E52020]" />
          <span className="text-sm font-mono font-bold text-[#1A1A1A]">{formatCurrency(stats.avg_cost)}</span>
          <span className="text-xs text-[#666666]">avg cost</span>
        </div>
        <div className="flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5 text-[#8B5CF6]" />
          <span className="text-sm font-mono font-bold text-[#1A1A1A]">{stats.avg_distance_km}</span>
          <span className="text-xs text-[#666666]">avg km</span>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="mt-4 space-y-4 border-t border-[#EBE6DC] pt-4">
          {/* Key metrics */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-[#FAFAF7] rounded-sm p-3">
              <div className="text-[10px] font-['Manrope'] font-bold text-[#666666] tracking-wider mb-1">AVG CONSUMPTION</div>
              <div className="text-lg font-mono font-bold text-[#1A1A1A]">{stats.avg_consumption} <span className="text-xs text-[#666666]">L/100km</span></div>
            </div>
            <div className="bg-[#FAFAF7] rounded-sm p-3">
              <div className="text-[10px] font-['Manrope'] font-bold text-[#666666] tracking-wider mb-1">AVG FUEL PRICE</div>
              <div className="text-lg font-mono font-bold text-[#1A1A1A]">{formatCurrency(stats.avg_fuel_price)} <span className="text-xs text-[#666666]">/L</span></div>
            </div>
            <div className="bg-[#FAFAF7] rounded-sm p-3">
              <div className="text-[10px] font-['Manrope'] font-bold text-[#666666] tracking-wider mb-1">TRIPS THIS WEEK</div>
              <div className="text-lg font-mono font-bold text-[#E52020]">{stats.trips_this_week}</div>
            </div>
            {topFuel && (
              <div className="bg-[#FAFAF7] rounded-sm p-3">
                <div className="text-[10px] font-['Manrope'] font-bold text-[#666666] tracking-wider mb-1">TOP FUEL TYPE</div>
                <div className="text-lg font-mono font-bold text-[#1A1A1A]">
                  <Fuel className="w-4 h-4 inline text-[#F59E0B] mr-1" />
                  {FUEL_TYPES[topFuel[0]]?.label || topFuel[0]}
                </div>
              </div>
            )}
          </div>

          {/* Fuel type breakdown */}
          {Object.keys(stats.fuel_type_breakdown || {}).length > 1 && (
            <div>
              <div className="text-[10px] font-['Manrope'] font-bold text-[#666666] tracking-wider mb-2">FUEL TYPE SPLIT</div>
              <div className="flex gap-1 h-4 rounded-full overflow-hidden">
                {Object.entries(stats.fuel_type_breakdown).map(([type, count]) => {
                  const pct = (count / stats.total_trips) * 100;
                  const colors = { diesel: '#E52020', unleaded_91: '#16A34A', unleaded_95: '#8B5CF6' };
                  return (
                    <div
                      key={type}
                      style={{ width: `${pct}%`, background: colors[type] || '#999999' }}
                      className="relative group"
                      title={`${FUEL_TYPES[type]?.label || type}: ${Math.round(pct)}%`}
                    />
                  );
                })}
              </div>
              <div className="flex gap-3 mt-1.5 flex-wrap">
                {Object.entries(stats.fuel_type_breakdown).map(([type, count]) => {
                  const colors = { diesel: '#E52020', unleaded_91: '#16A34A', unleaded_95: '#8B5CF6' };
                  return (
                    <div key={type} className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full" style={{ background: colors[type] || '#999999' }} />
                      <span className="text-[10px] text-[#666666]">{FUEL_TYPES[type]?.label || type} ({Math.round((count / stats.total_trips) * 100)}%)</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Popular routes */}
          {stats.popular_routes?.length > 0 && (
            <div>
              <div className="text-[10px] font-['Manrope'] font-bold text-[#666666] tracking-wider mb-2">POPULAR ROUTES</div>
              <div className="space-y-1.5">
                {stats.popular_routes.slice(0, 5).map((r, i) => (
                  <div key={`${r.origin}-${r.destination}-${i}`} className={`flex items-center justify-between py-2 px-2.5 rounded-sm ${i === 0 ? 'bg-[rgba(229, 32, 32,0.06)] border border-[rgba(229, 32, 32,0.15)]' : 'bg-[#FAFAF7]'}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[10px] font-bold text-[#999999] w-4">{i + 1}</span>
                      <span className="text-xs font-medium text-[#1A1A1A] truncate">{r.origin}</span>
                      <span className="text-[10px] text-[#999999]">&rarr;</span>
                      <span className="text-xs font-medium text-[#1A1A1A] truncate">{r.destination}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      <span className="text-[10px] text-[#666666]">{r.avg_distance}km</span>
                      <span className="font-mono text-xs font-bold text-[#1A1A1A]">{formatCurrency(r.avg_cost)}</span>
                      <span className="text-[9px] text-[#999999]">&times;{r.count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="text-[9px] text-[#999999] text-center italic">All data is fully anonymous — no personal information is collected</p>
        </div>
      )}
    </div>
  );
}
